// One patient's shared context (mockup layout, without risk scores: CLAUDE.md hard rule 3). The
// Manus panels (components/PatientPanels.tsx) add a summary, the recorded change in observations,
// a Clinician View and the risk cards as "not configured".
// Everything is shown as the source systems sent it. Replay steps through the visit's own
// events: the API returns the encounter as it was at each clinical time.

import type { BoardEntry, BoardTimer, OrderItem } from '@diagnuvo/domain';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { fetchEncounter, fetchVitals, NotFound, type EncounterView } from '../api.ts';
import { AcknowledgeButton } from '../components/AcknowledgeButton.tsx';
import {
  ageInYears,
  alertHandling,
  clockTime,
  countdown,
  displayName,
  hoursMinutes,
  nhsNumber,
  sexLetter,
  triageText,
  zoneLabel,
} from '../board-state.ts';
import { Banner, Card, ZoneDot } from '../components/Layout.tsx';
import {
  ClinicianView,
  DemoValuePanels,
  DynamicState,
  NotConfiguredPanels,
  SituationSummary,
} from '../components/PatientPanels.tsx';
import { DEMO_VALUE_SITES } from '../patient-panels.ts';
import { VitalTrends } from '../components/VitalTrends.tsx';
import {
  BackIcon,
  ClockIcon,
  DatabaseIcon,
  EyeIcon,
  FlaskIcon,
  HeartIcon,
  NextIcon,
  PauseIcon,
  PlayIcon,
  ResetIcon,
  ScanIcon,
} from '../icons.tsx';
import { href } from '../router.ts';

const INTERVALS = [2, 5, 10] as const;
/** The API serves at most 48 hours of trend. */
const TREND_MAX_MS = 48 * 3_600_000;
const TREND_DEFAULT_MS = 12 * 3_600_000;

export function PatientPage({
  siteId,
  encounterId,
  boardRow,
  now,
  canAcknowledge = false,
}: {
  siteId: string;
  encounterId: string;
  /** The live board row, when the patient is on the board: a change refreshes the page. */
  boardRow: BoardEntry | undefined;
  now: number;
  /** The user's role may acknowledge alerts (L4.2); the API checks again. */
  canAcknowledge?: boolean;
}) {
  const live = useQuery({
    queryKey: ['encounter', siteId, encounterId],
    queryFn: () => fetchEncounter(siteId, encounterId),
    retry: (count, error) => !(error instanceof NotFound) && count < 2,
  });
  const { refetch } = live;
  useEffect(() => {
    void refetch();
  }, [boardRow, refetch]);

  const [stage, setStage] = useState<number | undefined>(undefined);
  const [playing, setPlaying] = useState(false);
  const [interval, setIntervalSeconds] = useState<(typeof INTERVALS)[number]>(5);
  const [clinicianView, setClinicianView] = useState(false);
  const closeClinicianView = useCallback(() => setClinicianView(false), []);
  const stages = live.data?.timeline ?? [];
  const at = stage === undefined ? undefined : stages[stage]?.at;
  const replay = useQuery({
    queryKey: ['encounter', siteId, encounterId, at],
    queryFn: () => fetchEncounter(siteId, encounterId, at),
    enabled: at !== undefined,
    placeholderData: (previous) => previous,
  });

  // Trend from arrival (at most 48 hours back) to now, or to the replayed clinical time.
  const arrivedAt = live.data?.state.arrivedAt;
  const trendTo = at;
  const trendEnd = trendTo === undefined ? now : Date.parse(trendTo);
  const trendFrom = new Date(
    Math.max(
      arrivedAt === undefined ? trendEnd - TREND_DEFAULT_MS : Date.parse(arrivedAt),
      trendEnd - TREND_MAX_MS,
    ) - 60_000,
  ).toISOString();
  const trend = useQuery({
    queryKey: ['vitals', siteId, encounterId, trendTo ?? 'live', arrivedAt],
    queryFn: () => fetchVitals(siteId, encounterId, trendFrom, trendTo),
    enabled: live.data !== undefined,
    refetchInterval: trendTo === undefined ? 60_000 : false,
    placeholderData: (previous) => previous,
  });

  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => {
      setStage((s) => {
        const next = (s ?? -1) + 1;
        if (next >= stages.length - 1) setPlaying(false);
        return Math.min(next, stages.length - 1);
      });
    }, interval * 1000);
    return () => clearInterval(timer);
  }, [playing, interval, stages.length]);

  if (live.error instanceof NotFound) {
    return (
      <div className="page">
        <BackLink />
        <p className="empty">This visit was not found at this site.</p>
      </div>
    );
  }
  if (live.error !== null) {
    return (
      <div className="page">
        <BackLink />
        <p className="empty">The patient page is not available: {live.error.message}</p>
      </div>
    );
  }
  if (live.data === undefined) {
    return (
      <div className="page">
        <BackLink />
        <p className="empty">Loading the patient…</p>
      </div>
    );
  }

  const view: EncounterView = stage === undefined ? live.data : (replay.data ?? live.data);
  const shownAt = stage === undefined ? now : Date.parse(view.asOf ?? new Date(now).toISOString());
  const { state } = view;
  const p = state.patient;
  const age = ageInYears(p?.birthDate, shownAt);
  const where =
    state.leftEdAt !== undefined
      ? 'Left the ED'
      : state.status === 'discharged'
        ? 'Discharged'
        : state.status === 'cancelled'
          ? 'Arrival cancelled'
          : state.status === 'admitted'
            ? 'Admitted · awaiting bed'
            : state.status === 'arrived'
              ? 'In the ED'
              : 'Not yet arrived';
  const timer = view.timers.find((t) => t.id === 'ed-4h');
  const mode = stage === undefined ? 'Live' : playing ? 'Replaying' : 'Paused';
  const demo = DEMO_VALUE_SITES.has(siteId);

  return (
    <div className="page">
      <BackLink />
      <div className="page-head">
        <div>
          <div className="title-row">
            <span className="phase">PHASE 1</span>
            <h1>SHARED PATIENT CONTEXT</h1>
          </div>
          <p className="patient-line">
            <strong>{displayName(p)}</strong>
            {age !== undefined && (
              <span>
                {' '}
                · {age}
                {sexLetter(p?.gender)}
              </span>
            )}
            {p?.mrn !== undefined && <span className="muted"> · MRN {p.mrn}</span>}
            {p?.nhsNumber !== undefined && (
              <span className="muted"> · NHS {nhsNumber(p.nhsNumber)}</span>
            )}
          </p>
        </div>
        <div className="head-pills">
          <span className="pill pill-info">
            <ZoneDot code={state.location?.room} />
            {zoneLabel(state.location?.room)} · {state.location?.bed ?? '—'}
          </span>
          <span
            className={`pill ${state.leftEdAt !== undefined || state.status === 'discharged' ? 'pill-muted' : 'pill-ok'}`}
          >
            {where}
          </span>
          <button type="button" className="btn" onClick={() => setClinicianView(true)}>
            <EyeIcon /> Clinician View
          </button>
        </div>
      </div>

      <Banner>
        Synthetic demonstration data, shown as the hospital systems sent it. Not a prediction or a
        clinical decision: no risk score is calculated.
        {demo &&
          " The risk and score cards show predefined values of the Manus demonstration scenario, chosen by this visit's recorded milestones."}
      </Banner>

      <section className="card replay">
        <header className="replay-head">
          <div>
            <h2>CLINICAL REPLAY</h2>
            <p className="muted">
              {stage === undefined
                ? `Live · ${stages.length} recorded event${stages.length === 1 ? '' : 's'}`
                : `Event ${stage + 1} of ${stages.length} · ${clockTime(stages[stage]?.at ?? '')}`}
            </p>
          </div>
          <span className={`pill ${mode === 'Live' ? 'pill-ok' : 'pill-info'}`}>
            <span className="dot" /> {mode}
          </span>
          <div className="replay-controls">
            <button
              type="button"
              className="btn btn-primary"
              disabled={stages.length === 0}
              onClick={() => {
                if (playing) setPlaying(false);
                else {
                  if (stage === undefined || stage >= stages.length - 1) setStage(0);
                  setPlaying(true);
                }
              }}
            >
              {playing ? <PauseIcon /> : <PlayIcon />} {playing ? 'Pause' : 'Start replay'}
            </button>
            <button
              type="button"
              className="btn"
              disabled={stages.length === 0 || stage === stages.length - 1}
              onClick={() => {
                setPlaying(false);
                setStage((s) => Math.min((s ?? -1) + 1, stages.length - 1));
              }}
            >
              <NextIcon /> Next clinical event
            </button>
            <button
              type="button"
              className="btn"
              disabled={stage === undefined}
              onClick={() => {
                setPlaying(false);
                setStage(undefined);
              }}
            >
              <ResetIcon /> Back to live
            </button>
            <label className="interval">
              Interval
              <select
                value={interval}
                onChange={(e) =>
                  setIntervalSeconds(Number(e.target.value) as (typeof INTERVALS)[number])
                }
              >
                {INTERVALS.map((s) => (
                  <option key={s} value={s}>
                    {s} seconds
                  </option>
                ))}
              </select>
            </label>
          </div>
        </header>
        <div className="replay-panels">
          <div className="panel">
            <h4>Reason for attendance</h4>
            {state.complaint === undefined ? (
              <p className="muted">No presenting complaint recorded for this visit.</p>
            ) : (
              <p className="obs-line" data-testid="complaint">
                {state.complaint.text}
              </p>
            )}
            <p className="obs-line" data-testid="triage-category">
              {state.triage === undefined
                ? 'Not triaged yet.'
                : `Triage: ${triageText(state.triage)} · ${clockTime(state.triage.at)}`}
            </p>
            <p className="source">Source: patient administration (PAS), as sent</p>
          </div>
          <div className="panel">
            <h4>
              <HeartIcon size={16} /> Latest recorded observations
            </h4>
            {view.observations === undefined ? (
              <p className="muted">No observations recorded yet.</p>
            ) : (
              <>
                <p className="obs-line">
                  <strong>{clockTime(view.observations.at)}</strong>
                  {view.observations.values
                    .filter((v) => v.code !== 'NEWS2')
                    .map((v) => ` · ${short(v.label)} ${v.value}`)
                    .join('')}
                </p>
                <p className="source">
                  Source: observations system
                  {view.observations.device === undefined ? '' : ` (${view.observations.device})`}
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      <div className="grid-2">
        <SituationSummary view={view} />
        <DynamicState view={view} />
      </div>

      <div className="grid-3">
        <FourHourCard
          timer={timer}
          arrivedAt={state.arrivedAt}
          now={shownAt}
          {...(canAcknowledge &&
            stage === undefined && {
              acknowledge: { siteId, encounterId, onDone: () => void refetch() },
            })}
        />
        <Card
          title="Observations as received"
          icon={<HeartIcon />}
          info="Values exactly as the observations system sent them. Diagnuvo calculates nothing."
        >
          {view.observations === undefined ? (
            <p className="muted">No observations recorded yet.</p>
          ) : (
            <dl className="obs">
              {view.observations.values.map((v) => (
                <div key={v.code + v.label} className={v.code === 'NEWS2' ? 'obs-news2' : ''}>
                  <dt>{v.code === 'NEWS2' ? 'NEWS2 (as sent)' : v.label}</dt>
                  <dd>{v.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {view.observations?.values.some((v) => v.code === 'NEWS2') === true && (
            <p className="note">
              NEWS2 is the score the observations system sent. Diagnuvo does not calculate it.
            </p>
          )}
        </Card>
        <Card
          title="Orders and results"
          icon={<FlaskIcon />}
          info="Lab and imaging orders, their status, and results as the laboratory and radiology systems sent them."
        >
          <Orders title="Labs" icon={<FlaskIcon size={16} />} items={view.labs} />
          <Orders title="Imaging" icon={<ScanIcon size={16} />} items={view.imaging} />
        </Card>
      </div>

      <Card
        title="Vital-sign trends"
        icon={<HeartIcon />}
        info="Every value the observations system and bedside monitors sent, in 15-minute steps: the band runs from the lowest to the highest value, the line through the latest. Diagnuvo calculates and judges nothing."
      >
        {trend.data === undefined ? (
          <p className="muted">{trend.isError ? 'Trends are not available.' : 'Loading trends…'}</p>
        ) : (
          <VitalTrends series={trend.data.series} />
        )}
        <p className="note">Values as sent by the source systems. Not a clinical assessment.</p>
      </Card>

      {demo ? <DemoValuePanels view={view} /> : <NotConfiguredPanels />}

      <div className="grid-2">
        <Card
          title="Supporting clinical evidence"
          icon={<DatabaseIcon />}
          info="Which hospital systems sent data for this visit, and when they last did."
        >
          <ul className="sources">
            {view.sources.map((s) => (
              <li key={s.source}>
                <span>{s.label}</span>
                <span className="muted">
                  {s.events} event{s.events === 1 ? '' : 's'} · last {clockTime(s.last)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
        <Card
          title="Clinical clock"
          icon={<ClockIcon />}
          info="Every recorded event of this visit, in clinical time (the time it happened, not when it arrived)."
        >
          <ol className="clock">
            {stages.map((item, i) => {
              const pending = stage !== undefined && i > stage;
              return (
                <li
                  key={item.id}
                  className={`clock-item kind-${item.kind}${item.alert === undefined ? '' : ` alert-${item.alert.level}`}${pending ? ' pending' : ''}${stage === i ? ' current' : ''}`}
                >
                  <span className="clock-dot" />
                  <span className="clock-time">{clockTime(item.at)}</span>
                  <span className="clock-title">
                    {item.alert?.timerId === 'ed-4h'
                      ? `4-hour standard: ${item.title.toLowerCase()}`
                      : item.title}
                    {item.detail !== undefined && <span className="muted"> · {item.detail}</span>}
                  </span>
                  <span className={`pill ${pending ? 'pill-muted' : 'pill-done'}`}>
                    {pending ? 'Pending' : 'Recorded'}
                  </span>
                </li>
              );
            })}
          </ol>
        </Card>
      </div>

      {clinicianView && (
        <ClinicianView
          view={view}
          replaying={stage !== undefined}
          demo={demo}
          onClose={closeClinicianView}
        />
      )}
    </div>
  );
}

function BackLink() {
  return (
    <a className="back" href={href({ page: 'board' })}>
      <BackIcon /> ED board
    </a>
  );
}

const SHORT: Readonly<Record<string, string>> = {
  'Heart rate': 'HR',
  'Respiratory rate': 'RR',
  'Systolic blood pressure': 'SBP',
  'Diastolic blood pressure': 'DBP',
  'Blood pressure': 'BP',
  Temperature: 'Temp',
  'Supplemental oxygen': 'O₂',
};
const short = (label: string) => SHORT[label] ?? label;

function FourHourCard({
  timer,
  arrivedAt,
  now,
  acknowledge,
}: {
  timer: BoardTimer | undefined;
  arrivedAt: string | undefined;
  now: number;
  /** Set for a live (not replayed) page of a user who may acknowledge. */
  acknowledge?: { siteId: string; encounterId: string; onDone: () => void };
}) {
  if (timer === undefined) {
    return (
      <Card
        title="ED 4-hour standard"
        icon={<ClockIcon />}
        info="Arrival to leaving the ED. Warning at 3 hours, breach at 4."
      >
        <p className="muted">
          {arrivedAt === undefined ? 'No ED arrival recorded.' : 'Timer not started.'}
        </p>
      </Card>
    );
  }
  const start = Date.parse(timer.startedAt);
  const end = timer.stoppedAt === undefined ? now : Date.parse(timer.stoppedAt);
  const share = Math.min(Math.max((end - start) / (Date.parse(timer.breachAt) - start), 0), 1);
  const left = countdown(timer, now);
  const label =
    timer.alert === 'breach' ? 'Breached' : timer.alert === 'warning' ? 'Warning' : 'On track';
  return (
    <Card
      title="ED 4-hour standard"
      icon={<ClockIcon />}
      info="Arrival to leaving the ED. Warning at 3 hours, breach at 4. The alert is the one the rules engine recorded."
    >
      <div className="big-row">
        <span className="big">{hoursMinutes(end - start)}</span>
        <span
          className={`pill pill-${timer.alert === 'none' ? 'ok' : timer.alert === 'warning' ? 'warn' : 'danger'}`}
        >
          {label}
        </span>
      </div>
      <p className="muted">
        {timer.stoppedAt === undefined
          ? `in the ED · ${left.text}`
          : `stopped at ${clockTime(timer.stoppedAt)}`}
      </p>
      <span className="bar bar-lg" aria-hidden="true">
        <span className={`bar-fill fill-${timer.alert}`} style={{ width: `${share * 100}%` }} />
        <span className="bar-mark" />
      </span>
      <div className="bar-legend">
        <span>Arrived {clockTime(timer.startedAt)}</span>
        <span>Warning {clockTime(timer.warnAt)}</span>
        <span>Breach {clockTime(timer.breachAt)}</span>
      </div>
      {alertHandling(timer) !== undefined && (
        <p className="status-note" data-testid="alert-handling">
          {alertHandling(timer)}
        </p>
      )}
      {acknowledge !== undefined && (
        <AcknowledgeButton
          siteId={acknowledge.siteId}
          encounterId={acknowledge.encounterId}
          timer={timer}
          onDone={acknowledge.onDone}
        />
      )}
      <p className="note">Deterministic timer from clinical times. Not a clinical assessment.</p>
    </Card>
  );
}

const STATUS_LABELS: Readonly<Record<string, string>> = {
  ordered: 'Ordered',
  received: 'Sample received',
  preliminary: 'Preliminary',
  final: 'Final',
  corrected: 'Corrected',
  cancelled: 'Cancelled',
  started: 'In progress',
  completed: 'Completed',
  'reported-preliminary': 'Preliminary report',
  'reported-final': 'Reported',
};

function Orders({
  title,
  icon,
  items,
}: {
  title: string;
  icon: React.ReactNode;
  items: OrderItem[];
}) {
  return (
    <div className="orders">
      <h4>
        {icon} {title}
      </h4>
      {items.length === 0 ? (
        <p className="muted">None ordered.</p>
      ) : (
        <ul>
          {items.map((o) => (
            <li key={o.order}>
              <div className="order-top">
                <span>{o.name}</span>
                <span
                  className={`pill ${/final|reported-final|corrected/.test(o.status) ? 'pill-done' : o.status === 'cancelled' ? 'pill-muted' : 'pill-info'}`}
                >
                  {STATUS_LABELS[o.status] ?? o.status}
                </span>
              </div>
              {o.results.map((r) => (
                <div key={r.label} className="result">
                  <span className="muted">{r.label}</span>
                  <span>
                    {r.value}
                    {r.flag !== undefined && r.flag !== 'N' && (
                      <span className="flag"> {r.flag}</span>
                    )}
                  </span>
                </div>
              ))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
